/**
 * プライベートメモのパスフレーズ暗号化（PBKDF2 + AES-GCM）
 *
 * Web Crypto API（crypto.subtle）のみで実装し、外部依存を持たない。
 * - 鍵導出: PBKDF2-SHA256、600,000 回、salt 16 バイト
 * - 暗号化: AES-GCM 256 bit、IV 12 バイトを暗号化のたびに新規生成
 * - 暗号化単位: メモ全体の Record<serviceId, string> を JSON 化して 1 blob で暗号化
 *   （serviceId・件数もメタデータとして漏らさない）
 *
 * 保護範囲は「保存時の暗号化（at rest）」。解除中のメモリ上の鍵は XSS に対して無力なので、
 * UI・記事でこの限界を超える主張（「絶対安全」等)をしないこと。
 */

export const PBKDF2_ITERATIONS = 600_000;
export const MIN_PASSPHRASE_LENGTH = 8;
const SALT_BYTES = 16;
const IV_BYTES = 12;

export interface EnvelopeKdfParams {
  name: "PBKDF2";
  hash: "SHA-256";
  iterations: number;
  salt: string; // base64
}

/** localStorage["ai-deck-private-notes"] に保存する v2 封筒形式 */
export interface EncryptedNotesEnvelope {
  version: 2;
  kdf: EnvelopeKdfParams;
  payload: {
    iv: string; // base64
    ciphertext: string; // base64
  };
}

/**
 * 復号失敗（= パスフレーズ誤り、または封筒の破損）。
 * AES-GCM の認証エラーで検知できるため、呼び出し側はこの型でエラー表示を分岐する
 */
export class PrivateNotesDecryptionError extends Error {
  constructor(message = "復号に失敗しました（パスフレーズが違います）") {
    super(message);
    this.name = "PrivateNotesDecryptionError";
  }
}

function toBase64(bytes: Uint8Array): string {
  // 1 バイトずつの文字列連結は blob 全体で数万回の連結になるためチャンクで変換する
  const ChunkSize = 0x8000;
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += ChunkSize) {
    parts.push(String.fromCharCode(...bytes.subarray(i, i + ChunkSize)));
  }
  return btoa(parts.join(""));
}

function fromBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** 値が v2 封筒形式かを判定する（localStorage・インポートファイルの両方で使う） */
export function isEncryptedEnvelope(value: unknown): value is EncryptedNotesEnvelope {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<EncryptedNotesEnvelope>;
  return (
    candidate.version === 2 &&
    typeof candidate.kdf === "object" &&
    candidate.kdf !== null &&
    candidate.kdf.name === "PBKDF2" &&
    candidate.kdf.hash === "SHA-256" &&
    typeof candidate.kdf.iterations === "number" &&
    typeof candidate.kdf.salt === "string" &&
    typeof candidate.payload === "object" &&
    candidate.payload !== null &&
    typeof candidate.payload.iv === "string" &&
    typeof candidate.payload.ciphertext === "string"
  );
}

/** パスフレーズと KDF パラメータから AES-GCM 256 鍵を導出する（鍵は抽出不可・メモリのみ） */
export async function deriveKey(passphrase: string, kdf: EnvelopeKdfParams): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      hash: kdf.hash,
      salt: fromBase64(kdf.salt).buffer as ArrayBuffer,
      iterations: kdf.iterations,
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** 新規有効化用の KDF パラメータを生成する（salt は毎回新規） */
export function createKdfParams(): EnvelopeKdfParams {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  return {
    name: "PBKDF2",
    hash: "SHA-256",
    iterations: PBKDF2_ITERATIONS,
    salt: toBase64(salt),
  };
}

/** メモ全体を 1 blob で暗号化して封筒にする（IV は呼び出しごとに新規） */
export async function encryptNotes(
  notes: Record<string, string>,
  key: CryptoKey,
  kdf: EnvelopeKdfParams,
): Promise<EncryptedNotesEnvelope> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const plaintext = new TextEncoder().encode(JSON.stringify(notes));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv.buffer as ArrayBuffer },
    key,
    plaintext,
  );
  return {
    version: 2,
    kdf,
    payload: {
      iv: toBase64(iv),
      ciphertext: toBase64(new Uint8Array(ciphertext)),
    },
  };
}

/** 導出済みの鍵で封筒を復号する（タブ間同期の再復号用）。失敗は PrivateNotesDecryptionError */
export async function decryptNotesWithKey(
  envelope: EncryptedNotesEnvelope,
  key: CryptoKey,
): Promise<Record<string, string>> {
  let plaintext: ArrayBuffer;
  try {
    plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: fromBase64(envelope.payload.iv).buffer as ArrayBuffer,
      },
      key,
      fromBase64(envelope.payload.ciphertext).buffer as ArrayBuffer,
    );
  } catch {
    throw new PrivateNotesDecryptionError();
  }
  try {
    const parsed: unknown = JSON.parse(new TextDecoder().decode(plaintext));
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      throw new Error("invalid payload shape");
    }
    return parsed as Record<string, string>;
  } catch {
    throw new PrivateNotesDecryptionError();
  }
}

/** パスフレーズで封筒を復号する（ロック解除・暗号化バックアップの取り込み用） */
export async function decryptNotesWithPassphrase(
  envelope: EncryptedNotesEnvelope,
  passphrase: string,
): Promise<{ notes: Record<string, string>; key: CryptoKey }> {
  const key = await deriveKey(passphrase, envelope.kdf);
  const notes = await decryptNotesWithKey(envelope, key);
  return { notes, key };
}
